import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { listAdminUsers, postAdminUserRole, type AdminUserRow } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

export default function AdminUsersPage() {
  const { getBearerToken, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [role, setRole] = useState<"" | "teacher" | "student">("");
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [actingId, setActingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function onSearch(e: FormEvent) {
    e.preventDefault();
    const bearer = getBearerToken();
    if (!bearer) {
      setError("Sign in required");
      return;
    }
    const query = q.trim();
    if (query.length < 2) {
      setError("Enter at least 2 characters");
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const out = await listAdminUsers(bearer, {
        q: query,
        role: role || undefined,
      });
      setUsers(out.users);
    } catch (err) {
      setUsers([]);
      setError(err instanceof Error ? err.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  async function onImpersonate(user: AdminUserRow) {
    setActingId(user.id);
    setError(null);
    setNotice(null);
    try {
      const targetRole = await startImpersonation(user.id);
      navigate(targetRole === "student" ? "/lessons" : "/teacher", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impersonation failed");
      setActingId(null);
    }
  }

  async function onMakeStudent(user: AdminUserRow) {
    const bearer = getBearerToken();
    if (!bearer) {
      setError("Sign in required");
      return;
    }
    const ok = window.confirm(
      `Change ${user.email} from teacher to student? They cannot undo this themselves. They must sign out and sign in again, then they can use a join code.`
    );
    if (!ok) return;
    setActingId(`role:${user.id}`);
    setError(null);
    setNotice(null);
    try {
      const updated = await postAdminUserRole(bearer, user.id, "student");
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, role: updated.role } : u)));
      setNotice(`${updated.email} is now a student. Ask them to sign in again.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Role change failed");
    } finally {
      setActingId(null);
    }
  }

  return (
    <section className="card card-wide">
      <h1 className="teacher-panel-title">Users</h1>
      <p className="muted">
        Search by email, then impersonate a teacher or student, or correct a mistaken teacher signup to
        student. You cannot impersonate admins.
      </p>

      <form className="admin-users-search" onSubmit={(e) => void onSearch(e)}>
        <label className="field">
          <span>Email</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="name@school.org"
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as "" | "teacher" | "student")}>
            <option value="">Any</option>
            <option value="teacher">Teacher</option>
            <option value="student">Student</option>
          </select>
        </label>
        <button type="submit" className="btn" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error ? (
        <p className="warn-banner" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="muted" role="status">
          {notice}
        </p>
      ) : null}

      {users.length > 0 ? (
        <div className="admin-users-table-wrap">
          <table className="admin-users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.role}</td>
                  <td>
                    <div className="admin-users-actions">
                      <button
                        type="button"
                        className="btn tight"
                        disabled={actingId === u.id || actingId === `role:${u.id}`}
                        onClick={() => void onImpersonate(u)}
                      >
                        {actingId === u.id ? "Starting…" : "Impersonate"}
                      </button>
                      {u.role === "teacher" ? (
                        <button
                          type="button"
                          className="btn tight secondary"
                          disabled={actingId === u.id || actingId === `role:${u.id}`}
                          onClick={() => void onMakeStudent(u)}
                        >
                          {actingId === `role:${u.id}` ? "Saving…" : "Make student"}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!loading && !error && users.length === 0 && q.trim().length >= 2 ? (
        <p className="muted">No matching teachers or students.</p>
      ) : null}
    </section>
  );
}
